import { getOAuthSettings } from './getOAuthSettings';
import { API } from '../swagger/collections';
import { IdentityAPI } from '../swagger/collections-identity';
import { getItem, LSKey, removeItem, setItem } from '../store/localStore';
import axios, { AxiosError } from 'axios';
import { msalInstance, msalReady } from './msalInstance';
import { microsoftLoginRequest } from './msalConfig';
import { BaseAuth, TokenExchangeResponse } from '@ydistri/utils';

const { oauthUrl, clientId, oauthScopes, microsoftClientId, microsoftScopes } = getOAuthSettings();

/** Error format after incorrect email/password  */
interface WrongEmailOrPassword {
  error: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- required format
  error_description: string;
}

export default class Auth extends BaseAuth<LSKey> {
  constructor() {
    const getStringItem = (key: LSKey): string | undefined => {
      const value = getItem(key);
      return typeof value === 'string' ? value : undefined;
    };

    const setStringItem = (key: LSKey, value: string | undefined): void => {
      setItem(key, value);
    };

    const removeStoredItem = (key: LSKey): void => {
      removeItem(key);
    };

    super({
      oauthUrl,
      oauthClientId: clientId,
      oauthScope: oauthScopes.join(' '),
      microsoftClientId,
      microsoftScopes,
      missingMicrosoftClientIdMessage:
        'Missing Microsoft client id. Set VITE_REACT_APP_MICROSOFT_CLIENT_ID or VITE_REACT_APP_AUTH_CLIENT_ID.',
      missingMicrosoftScopesMessage:
        'Missing Microsoft scopes. Set VITE_REACT_APP_MICROSOFT_AUTH_SCOPES or VITE_REACT_APP_AUTH_SCOPES.',
      storage: {
        getItem: getStringItem,
        setItem: setStringItem,
        removeItem: removeStoredItem,
      },
      keys: {
        token: LSKey.TOKEN,
        tokenExpiration: LSKey.TOKEN_EXPIRATION,
        refreshToken: LSKey.REFRESH_TOKEN,
        msalAccountHomeId: LSKey.MSAL_ACCOUNT_HOME_ID,
      },
      msalInstance,
      msalReady,
      microsoftLoginRequest,
    });
  }

  getUser(): string | undefined {
    return getItem(LSKey.TOKEN);
  }

  syncAuthenticationHeadersFromStoredToken(): void {
    super.syncAuthenticationHeadersFromStoredToken();
  }

  signin(username: string, password: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!clientId) {
        reject(
          new Error(
            'Missing OAuth client id. Set VITE_REACT_APP_OAUTH_CLIENT_ID in your frontend environment.',
          ),
        );
        return;
      }

      const params = new URLSearchParams();
      params.append('client_id', clientId);
      params.append('scope', oauthScopes.join(' '));
      params.append('grant_type', 'password');
      params.append('username', username);
      params.append('password', password);

      axios({
        method: 'POST',
        // eslint-disable-next-line @typescript-eslint/naming-convention -- header name with capital
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        url: `${oauthUrl}/connect/token`,
        data: params,
      })
        .then((res: { data: TokenExchangeResponse }) => {
          this.setSession({
            expiresIn: res.data.expires_in,
            accessToken: res.data.access_token,
            refreshToken: res.data.refresh_token,
          });
          resolve();
        })
        .catch((e: unknown) => {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- required
          const error = e as AxiosError<object | WrongEmailOrPassword>;

          let errorMessage = error.message;
          const errorResponseData = error.response?.data;

          if (
            errorResponseData &&
            'error_description' in errorResponseData &&
            errorResponseData.error_description === 'invalid_username_or_password'
          ) {
            errorMessage = 'Invalid email or password.';
          }

          reject(new Error(errorMessage));
        });
    });
  }

  protected onSignOut(): void {
    removeItem(LSKey.USER);
  }

  protected handleExpiredToken(): void {
    this.signOut();
    window.location.reload();
  }

  protected setAuthenticationHeaders(token: string): void {
    const bearer = `Bearer ${token}`;

    API.instance.defaults.headers.common.Authorization = bearer;
    IdentityAPI.instance.defaults.headers.common.Authorization = bearer;
  }

  protected clearAuthenticationHeaders(): void {
    delete API.instance.defaults.headers.common.Authorization;
    delete IdentityAPI.instance.defaults.headers.common.Authorization;
  }
}
